<script lang="ts">
  import Skeleton from './Skeleton.svelte';
  import { ChevronLeft } from '@lucide/svelte';
  import type { Component, Snippet } from 'svelte';
  import { fa } from '../i18n/fa';
  import { cn } from '../lib/cn';

  /**
   * One line of the home's «needs follow-up» card (docs/requests/2026-10-18-report.md). It keeps its
   * place whether or not something waits, so the page does not jump: a calm line stays quiet, and
   * only a line with something waiting takes the warning ground and names where its link goes. While
   * the data is on its way it says so; when the read fails it says that and offers to read again,
   * never a calm «nothing to do» in place of an error.
   */
  let {
    to,
    title,
    Icon,
    attention = false,
    loading = false,
    error = null,
    onRetry,
    linkLabel,
    children,
    testid,
  }: {
    to: string;
    title: string;
    Icon: Component<{ class?: string }>;
    /** Something waits for someone: the icon takes the warning ground and the link is named. */
    attention?: boolean;
    loading?: boolean;
    error?: unknown;
    onRetry?: () => void;
    /** Where the link goes, said when something waits («مشاهده‌ی صف ارسال»). */
    linkLabel?: string;
    children?: Snippet;
    testid?: string;
  } = $props();

  const warn = $derived(attention && !error && !loading);
</script>

<li class="flex items-start gap-3 py-3" data-testid={testid}>
  <span
    class={cn('mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md', warn ? 'bg-warn-soft text-warn' : 'bg-surface-2 text-text-3')}
    aria-hidden="true"
  >
    <Icon class="size-4" />
  </span>
  <div class="min-w-0 flex-1">
    <h3>
      <a href={to} class="inline-flex items-center gap-1 text-sm font-bold hover:underline"
        >{title}<ChevronLeft class="size-3.5 text-text-4" aria-hidden="true" /></a
      >
    </h3>
    <div class="mt-0.5 text-sm text-text-2">
      {#if loading}
        <p class="pt-1"><span class="sr-only">{fa.app.loading}</span><Skeleton class="h-3.5 w-2/3" /></p>
      {:else if error}
        <p class="flex flex-wrap items-center gap-x-2 text-danger" role="alert">
          {fa.home.cardFailed}{#if onRetry}<button type="button" class="min-h-9 rounded-md px-1 text-accent underline-offset-4 hover:underline" onclick={onRetry}
              >{fa.app.retry}</button
            >{/if}
        </p>
      {:else}
        {@render children?.()}{#if warn && linkLabel}<a href={to} class="mt-1 inline-flex min-h-9 items-center gap-1 text-accent hover:underline"
            >{linkLabel}<ChevronLeft class="size-4" aria-hidden="true" /></a
          >{/if}
      {/if}
    </div>
  </div>
</li>
